import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import type { TCoverageTableWCMessageVariants } from 'common/constants';
import { CoverageDetails } from 'components/CoverageDetails';
import type { CoverageTableDataRow } from 'components/CoverageTable/CoverageTable.types';
import { CoverageTableHeader } from 'components/CoverageTable/components/CoverageTableHeader';
import { currencyFormat } from 'helpers/utils';
import { isNumber } from 'lodash-es';
import theme from 'providers/theme/theme';
import { type FC, Fragment } from 'react';
import CoverageTableWCMessage from '../CoverageTableWCMessage/CoverageTableWCMessage';
import {
  CoverageTableDesktopFirstColumnStackStyle,
  CoverageTableDesktopNoWCCellStyle,
  CoverageTableDesktopStyle,
  CoverageTableDesktopWCCellStyle,
} from './CoverageTableDesktop.styles';
import type { CoverageTableDesktopProps } from './CoverageTableDesktop.types';

const columns: GridColDef<CoverageTableDataRow>[] = [
  {
    field: 'coverages',
    headerName: '',
    resizable: false,
    flex: 1,
    type: 'string',
    disableColumnMenu: true,
    cellClassName: ({ row }) =>
      row.id === 'workers_compensation_message' ? 'workers_compensation_message' : '',
    colSpan: (_, row) => (row.id === 'workers_compensation_message' ? 3 : 1),
    renderCell: (params) => {
      const { coverages, additionalData, id, additionalValues } = params.row;

      if (id === 'workers_compensation_message') {
        return <CoverageTableWCMessage messageVariant={additionalValues?.messageVariant} />;
      }

      return (
        <Stack sx={CoverageTableDesktopFirstColumnStackStyle}>
          {coverages && <CoverageDetails {...coverages} />}

          <Stack mt={3}>
            {additionalData?.map((item) => (
              <CoverageDetails key={item.listKey || item.triggerText} {...item} />
            ))}
          </Stack>
        </Stack>
      );
    },
  },
  {
    field: 'limit',
    headerName: 'Limit',
    flex: 1,
    maxWidth: 176,
    resizable: false,
    align: 'right',
    headerAlign: 'right',
    disableColumnMenu: true,
    renderHeader: (params) => (
      <Typography variant="h3" textTransform="capitalize">
        {params.field}
      </Typography>
    ),
    renderCell: (params) => {
      const { limit } = params.row;

      return (
        <Stack>
          {limit?.map(({ label, value }, index) => (
            <Box key={label || index}>
              <Typography variant="body1">
                {isNumber(value) ? currencyFormat(value, undefined, true).merged : value}
              </Typography>

              <Typography variant="body2" color={theme.palette.customColors.darkGray}>
                {label || ''}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    },
  },
  {
    field: 'deductible',
    headerName: 'Deductible',
    flex: 1,
    maxWidth: 176,
    resizable: false,
    align: 'right',
    headerAlign: 'right',
    disableColumnMenu: true,
    renderHeader: (params) => (
      <Typography variant="h3" textTransform="capitalize">
        {params.field}
      </Typography>
    ),
    renderCell: (params) => {
      const { deductible } = params.row;

      return (
        <Stack>
          {deductible?.map(({ label, value }, index) => (
            <Box key={label || index}>
              <Typography variant="body1">
                {isNumber(value) ? currencyFormat(value, undefined, true).merged : value}
              </Typography>

              <Typography variant="body2" color={theme.palette.customColors.darkGray}>
                {label || ''}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    },
  },
];

const CoverageTableDesktop: FC<CoverageTableDesktopProps> = ({ tablesData }) =>
  tablesData.map((tableData, index) => {
    const isCurrentRowWC = tableData.title === 'Your Employees';
    let displayCols = true;
    let wcMessageVariant: TCoverageTableWCMessageVariants = 'no_message';

    if (isCurrentRowWC) {
      const wcMessageRow = tableData.rows?.find((row) => row.id === 'workers_compensation_message');

      wcMessageVariant = wcMessageRow?.additionalValues?.messageVariant;

      if (
        wcMessageRow &&
        (wcMessageVariant === 'without_wc' || wcMessageVariant === 'wc_not_available')
      ) {
        displayCols = false;
      }
    }

    return (
      <Fragment key={tableData.title}>
        <CoverageTableHeader tableData={tableData} isFirstHeader={index === 0} />

        {/* render WC warning message if current cell is WC & the product is WITHOUT_WC */}
        {isCurrentRowWC && !displayCols && (
          <TableRow>
            <TableCell sx={CoverageTableDesktopNoWCCellStyle}>
              <CoverageTableWCMessage messageVariant={wcMessageVariant} />
            </TableCell>
          </TableRow>
        )}

        {displayCols && (
          <DataGrid
            rows={tableData.rows}
            columns={columns}
            hideFooter
            autoHeight
            disableColumnFilter
            disableColumnSorting
            sx={[
              CoverageTableDesktopStyle,
              ...(isCurrentRowWC && wcMessageVariant === 'with_wc_ex_oo'
                ? [CoverageTableDesktopWCCellStyle]
                : []),
            ]}
            getRowHeight={() => 'auto'}
          />
        )}
      </Fragment>
    );
  });

export default CoverageTableDesktop;
